import React, { useState, useEffect } from "react";
import Header from "./Header";
import Footer from "./Footer";
import WhatsAppWidget from "./WhatsAppWidget";
import CookieBanner from "./CookieBanner";
import { Smartphone, Star, ArrowRight, Zap, Camera, Battery, CreditCard, Gift, Monitor, CheckCircle2 } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

const DispositiviPage = () => {
  const location = useLocation();
  const [showCookieBanner, setShowCookieBanner] = useState(() => {
    return !localStorage.getItem('cookiesAccepted');
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const smartphones = [
    {
      brand: "Samsung",
      model: "Galaxy S25 Ultra",
      price: "33,00",
      originalPrice: "38,00",
      storage: "512GB",
      image: "https://customer-assets.emergentagent.com/job_site-redesign-7/artifacts/nxb49kvi_s25%20ultra.png",
      features: [
        "Processore Snapdragon 8 Gen 4",
        "Galaxy AI Avanzata",
        "Ricarica Ultrarapida 3.0",
        "S Pen Integrata"
      ],
      plans: ["Super Unlimited Plus"],
      rating: 5,
      popular: true,
      promo: "Raddoppia la memoria"
    },
    {
      brand: "Samsung",
      model: "Galaxy S25",
      price: "21,00",
      originalPrice: "26,00",
      storage: "256GB",
      image: "https://customer-assets.emergentagent.com/job_site-redesign-7/artifacts/plcnrxws_s25.jpg",
      features: [
        "Design Sottile e Moderno",
        "Display Dynamic AMOLED 2X",
        "Tripla Fotocamera 50MP",
        "AI Photo Assist"
      ],
      plans: ["Super Unlimited"],
      rating: 5,
      promo: "Promo Lancio"
    },
    {
      brand: "Motorola",
      model: "Edge 50 Fusion",
      price: "8,00",
      originalPrice: "12,90",
      storage: "256GB",
      image: "https://customer-assets.emergentagent.com/job_site-redesign-7/artifacts/9dmi82sa_motorola%20g56.jpg",
      features: [
        "Impermeabilità IP68",
        "Ricarica TurboPower 68W",
        "Display 144Hz curvo",
        "Fotocamera Sony LYTIA"
      ],
      plans: ["Offerte Super"],
      rating: 4,
      special: "Anticipo ZERO"
    },
    {
      brand: "TCL",
      model: "60R 5G",
      price: "5,00",
      originalPrice: "8,00",
      storage: "128GB",
      image: "https://customer-assets.emergentagent.com/job_site-redesign-7/artifacts/qpxr3rz0_TCL%2060R.webp",
      features: [
        "Display NXTPAPER 3.0",
        "Eye Comfort Technology",
        "Ideale per il lavoro",
        "Batteria 5010mAh"
      ],
      plans: ["Offerte Super Voce"],
      rating: 4,
      special: "Anticipo ZERO"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Dynamic Hero Section */}
      <section className="relative pt-40 pb-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50 via-white to-orange-50/30" />
        <div className="container relative z-10 mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-orange-100 rounded-full text-orange-600 mb-8">
              <Smartphone className="w-4 h-4" />
              <span className="text-xs font-black uppercase tracking-widest">Listino Business 2026</span>
            </div>
            <h1 className="text-6xl md:text-8xl font-black text-gray-900 mb-8 tracking-tighter leading-none">
              La Potenza del <span className="text-orange-500">Business</span> nelle tue mani.
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Scegli i migliori dispositivi per la tua azienda con rateizzazione a tasso zero e i piani WindTre Business.
            </p>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <div className="border-y border-gray-100 py-12 bg-gray-50/50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { icon: <CheckCircle2 className="w-5 h-5 text-green-500" />, text: "Anticipo Zero" },
              { icon: <CheckCircle2 className="w-5 h-5 text-green-500" />, text: "Tasso Zero" },
              { icon: <CheckCircle2 className="w-5 h-5 text-green-500" />, text: "Assicurazione Kasko" },
              { icon: <CheckCircle2 className="w-5 h-5 text-green-500" />, text: "Consegna Gratuita" }
            ].map((badge, i) => (
              <div key={i} className="flex items-center justify-center space-x-3">
                {badge.icon}
                <span className="font-bold text-gray-800 tracking-tight uppercase text-sm">{badge.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Main Grid */}
      <section className="py-32">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20">
            {smartphones.map((phone, index) => (
              <div key={index} className="group relative">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                  {/* Image Side */}
                  <div className="lg:col-span-5 relative">
                    <div className="aspect-[4/5] bg-gray-50 rounded-[3rem] overflow-hidden group-hover:shadow-2xl transition-all duration-700">
                      <img 
                        src={phone.image} 
                        alt={phone.model}
                        className="w-full h-full object-contain p-8 group-hover:scale-110 transition-transform duration-700"
                      />
                    </div>
                    {phone.popular && (
                      <div className="absolute -top-4 -right-4 bg-orange-500 text-white px-6 py-2 rounded-full text-xs font-black shadow-xl uppercase">
                        Top Seller
                      </div>
                    )}
                  </div>

                  {/* Content Side */}
                  <div className="lg:col-span-7">
                    <span className="text-orange-500 font-black uppercase tracking-[0.3em] text-xs mb-4 block">{phone.brand}</span>
                    <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-6 tracking-tighter">{phone.model}</h2>
                    
                    <ul className="space-y-4 mb-10">
                      {phone.features.map((feature, i) => (
                        <li key={i} className="flex items-start space-x-3 text-gray-600">
                          <Zap className="w-4 h-4 text-orange-400 mt-1 flex-shrink-0" />
                          <span className="text-lg font-medium">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <div className="flex items-end space-x-4 mb-10">
                      <div>
                        <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest block mb-1">Da soli</span>
                        <div className="flex items-baseline space-x-1">
                          <span className="text-6xl font-black text-gray-900">{phone.price}</span>
                          <span className="text-xl font-bold text-gray-400 uppercase">€/mese</span>
                        </div>
                      </div>
                      {phone.originalPrice && (
                        <div className="pb-2">
                          <span className="text-lg text-gray-300 line-through font-bold">{phone.originalPrice}€</span>
                        </div>
                      )}
                    </div>

                    <Link 
                      to="/preventivo"
                      className="inline-flex items-center justify-center bg-gray-900 text-white px-10 py-5 rounded-2xl font-black text-lg hover:bg-orange-500 transition-all duration-300 group/btn"
                    >
                      Configura Business
                      <ArrowRight className="ml-3 w-5 h-5 group-hover/btn:translate-x-2 transition-transform" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Professional Banner */}
      <section className="container mx-auto px-4 pb-32">
        <div className="bg-gray-900 rounded-[3rem] p-12 md:p-24 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/20 rounded-full blur-[120px] -mr-48 -mt-48" />
          <div className="relative z-10 grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-5xl md:text-6xl font-black text-white mb-8 tracking-tighter leading-tight">
                Digitalizza la tua <span className="text-orange-500">Impresa</span> con Lovecchio.
              </h2>
              <p className="text-xl text-gray-400 mb-12 leading-relaxed">
                Non solo smartphone: offriamo consulenza su misura per connettività, ICT e soluzioni cloud.
              </p>
              <div className="flex flex-wrap gap-6">
                <Link to="/preventivo" className="bg-white text-black px-10 py-5 rounded-2xl font-black hover:bg-orange-500 hover:text-white transition-all">
                  Parla con un Esperto
                </Link>
                <Link to="/servizi" className="border border-white/20 text-white px-10 py-5 rounded-2xl font-black hover:bg-white/10 transition-all">
                  Scopri i Servizi
                </Link>
              </div>
            </div>
            <div className="hidden md:block">
              <div className="grid grid-cols-2 gap-6">
                {[
                  { icon: <Monitor />, label: "Monitoraggio" },
                  { icon: <CreditCard />, label: "Leasing" },
                  { icon: <Battery />, label: "Sostenibilità" },
                  { icon: <Zap />, label: "Performance" }
                ].map((item, i) => (
                  <div key={i} className="bg-white/5 border border-white/10 p-8 rounded-3xl backdrop-blur-md">
                    <div className="text-orange-500 w-8 h-8 mb-4">{item.icon}</div>
                    <span className="text-white font-bold">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppWidget />
      {showCookieBanner && (
        <CookieBanner onAccept={() => {
          localStorage.setItem('cookiesAccepted', 'true');
          setShowCookieBanner(false);
        }} />
      )}
    </div>
  );
};

export default DispositiviPage;
